import React from 'react';
import { Box, Button, Divider, List, ListItem, ListItemText, Paper, Typography, Alert } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { getUpcomingEvents, CalendarEvent } from '../services/calendarService';
import { listMyMeetings, syncMeetings } from '../services/sttApi';
import { useAuth } from '../contexts/AuthContext';
import { isSttSupported, startStt, SttHandle } from '../services/sttService';
import {
  joinSttMeeting,
  leaveSttMeeting,
  onSttPartial,
  onSttFinal,
  onSttHistory,
  sendSttPartial,
  sendSttFinal,
  startSttSession,
  stopSttSession,
  SttIncoming,
  SttHistoryEntry,
} from '../services/realtimeService';
import { useLocation } from 'react-router-dom';

interface TranscriptEntry {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  text: string;
  atIso: string;
  final: boolean;
}

const TranscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [events, setEvents] = React.useState<CalendarEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = React.useState<boolean>(false);
  const [activeMeetingId, setActiveMeetingId] = React.useState<string | null>(null);
  const [selectedMeetingId, setSelectedMeetingId] = React.useState<string | null>(null);
  const [isRecording, setIsRecording] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<TranscriptEntry[]>([]);
  const sttRef = React.useRef<SttHandle | null>(null);
  const localMapRef = React.useRef<Map<string, string>>(new Map()); // localId -> transcriptEntryId
  const keepAliveRef = React.useRef<any>(null);
  const activeMeetingIdRef = React.useRef<string | null>(null);
  const passiveJoinedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingEvents(true);
      try {
        // Najpierw spróbuj zsynchronizować i pobrać z bazy
        try { await syncMeetings(); } catch {}
        try {
          const planned = await listMyMeetings('planned');
          const ended = await listMyMeetings('ended');
          const merged = [...planned, ...ended].map(m => ({
            id: m.id,
            summary: m.summary,
            start: m.start,
            end: m.end,
            htmlLink: m.htmlLink
          })) as CalendarEvent[];
          if (mounted && merged.length > 0) {
            setEvents(merged);
          } else {
            // fallback do bezpośredniego kalendarza
            const list = await getUpcomingEvents(7, 20);
            if (mounted) setEvents(list || []);
          }
        } catch {
          const list = await getUpcomingEvents(7, 20);
          if (mounted) setEvents(list || []);
        }
      } finally {
        setLoadingEvents(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Preselect/autostart z query: ?meeting=...&autostart=1
  const autoHandledRef = React.useRef(false);
  React.useEffect(() => {
    try {
      if (autoHandledRef.current) return;
      const q = new URLSearchParams(location.search);
      const m = q.get('meeting');
      const autostart = (q.get('autostart') || '') === '1';
      if (!m) return;
      setSelectedMeetingId(m);
      const ev = (events || []).find(e => e.id === m);
      if (autostart && ev && isEligible(ev.start)) {
        autoHandledRef.current = true;
        setTimeout(() => handleStart(m), 100);
      }
    } catch {}
  }, [location.search, events]); 

  const isEligible = (startIso?: string | null) => {
    if (!startIso) return false;
    const start = new Date(startIso).getTime();
    if (!Number.isFinite(start)) return false;
    const now = Date.now();
    const fiveMinBefore = start - 5 * 60 * 1000;
    const twoHoursAfter = start + 120 * 60 * 1000;
    return now >= fiveMinBefore && now <= twoHoursAfter;
  };

  // Dołącz pasywnie, aby pobrać historię dla wybranego spotkania
  React.useEffect(() => {
    if (!selectedMeetingId) return;
    if (selectedMeetingId === activeMeetingIdRef.current) return;
    try {
      const prev = passiveJoinedRef.current;
      if (prev && prev !== activeMeetingIdRef.current) {
        leaveSttMeeting(prev);
      }
      passiveJoinedRef.current = selectedMeetingId;
      setTranscript([]);
      joinSttMeeting(selectedMeetingId);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeetingId]);

  const handleStart = (meetingId: string) => {
    setActiveMeetingId(meetingId);
    activeMeetingIdRef.current = meetingId;
    setTranscript([]); // wyczyść lokalny widok – historia przyjdzie z serwera
    setIsRecording(true);
    // Dołączenie do pokoju + zgłoszenie globalnej sesji (dla innych uczestników w topbar)
    try { joinSttMeeting(meetingId); } catch {}
    try { startSttSession(meetingId); } catch {}
    // Start STT lokalnie (Web Speech API)
    if (!isSttSupported()) {
      addSystemInfo('Ta przeglądarka nie wspiera rozpoznawania mowy. Użyj najnowszego Chrome.');
      return;
    }
    const genLocalId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try { localStorage.setItem('stt:recording', '1'); } catch {}
    try { localStorage.setItem('stt:recording_at', String(Date.now())); } catch {}
    try { window.dispatchEvent(new CustomEvent('stt:recording', { detail: { active: true, meetingId } })); } catch {}
    const deviceId = (() => { try { return localStorage.getItem('stt:deviceId') || undefined; } catch { return undefined; } })();
    const meDisplay = user?.display_name || user?.email || 'Ja';
    const meAvatar = user?.avatar_url || undefined;
    // heartbeat aktualizujący znacznik aktywności
    try {
      if (keepAliveRef.current) { try { clearInterval(keepAliveRef.current); } catch {} }
      keepAliveRef.current = setInterval(() => {
        try { localStorage.setItem('stt:recording_at', String(Date.now())); } catch {}
      }, 1500);
    } catch {}
    sttRef.current = startStt('pl-PL', {
      onPartial: (text, localId) => {
        const id = localId || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
        // lokalny echo
        upsertTranscript({
          meetingId,
          userId: user?.user_id || 'me',
          displayName: meDisplay,
          avatarUrl: meAvatar,
          text,
          atIso: new Date().toISOString(),
          localId: id
        }, false);
        try { sendSttPartial(meetingId, text, id); } catch {}
      },
      onFinal: (text, localId) => {
        const id = localId || `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
        // lokalny echo (final)
        upsertTranscript({
          meetingId,
          userId: user?.user_id || 'me',
          displayName: meDisplay,
          avatarUrl: meAvatar,
          text,
          atIso: new Date().toISOString(),
          localId: id
        }, true);
        try { sendSttFinal(meetingId, text, id); } catch {}
      },
      onError: (err) => {
        const msg = (err?.message || '').toString();
        // Tłumimy „no-speech” i „aborted” – auto-restart zajmie się tym
        if (/no-speech|aborted/i.test(msg)) return;
        addSystemInfo(`Błąd STT: ${msg || String(err)}`);
      }
    }, { continuous: true, interimResults: true, prewarmDeviceId: deviceId });
  };

  const handleStop = () => {
    setIsRecording(false);
    // zatrzymanie STT + leave room
    try { sttRef.current?.stop(); } catch {}
    if (activeMeetingId) {
      try { stopSttSession(activeMeetingId); } catch {}
      try { leaveSttMeeting(activeMeetingId); } catch {}
    }
    activeMeetingIdRef.current = null;
    try { localStorage.setItem('stt:recording', '0'); } catch {}
    try { localStorage.removeItem('stt:recording_at'); } catch {}
    try { window.dispatchEvent(new CustomEvent('stt:recording', { detail: { active: false, meetingId: activeMeetingId } })); } catch {}
    try { if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; } } catch {}
  };

  // Obsługa napływających eventów STT
  React.useEffect(() => {
    const offHistory = onSttHistory((meetingId, items) => {
      if (meetingId !== activeMeetingId && meetingId !== selectedMeetingId) return;
      setTranscript(() => {
        const mapped: TranscriptEntry[] = (items as SttHistoryEntry[]).map((t) => ({
          id: `${t.userId}-${t.atIso || Math.random().toString(36).slice(2, 6)}`,
          userId: t.userId,
          displayName: t.displayName || t.userId,
          avatarUrl: t.avatarUrl || undefined,
          text: t.text,
          atIso: t.atIso || new Date().toISOString(),
          final: true
        }));
        return mapped;
      });
    });
    const offPartial = onSttPartial((p: SttIncoming) => {
      if (p.meetingId !== activeMeetingId && p.meetingId !== selectedMeetingId) return;
      upsertTranscript(p, false);
    });
    const offFinal = onSttFinal((p: SttIncoming) => {
      if (p.meetingId !== activeMeetingId && p.meetingId !== selectedMeetingId) return;
      upsertTranscript(p, true);
    });
    return () => {
      try { offHistory && (offHistory as any)(); } catch {}
      try { offPartial && (offPartial as any)(); } catch {}
      try { offFinal && (offFinal as any)(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMeetingId, selectedMeetingId]);

  const upsertTranscript = (p: SttIncoming, makeFinal: boolean) => {
    setTranscript((prev) => {
      const idFromLocal = p.localId ? localMapRef.current.get(p.localId) : null;
      if (idFromLocal) {
        return prev.map((e) => e.id === idFromLocal ? { ...e, text: p.text, final: makeFinal, atIso: p.atIso || e.atIso } : e);
      }
      const newId = `${p.userId}-${p.atIso || Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      if (p.localId) localMapRef.current.set(p.localId, newId);
      const entry: TranscriptEntry = {
        id: newId,
        userId: p.userId,
        displayName: p.displayName || p.userId,
        avatarUrl: p.avatarUrl || undefined,
        text: p.text,
        atIso: p.atIso || new Date().toISOString(),
        final: makeFinal
      };
      return [...prev, entry];
    });
  };

  // Sprzątanie przy opuszczaniu strony/karty
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      try {
        if (e.key !== 'stt:stop') return;
        const payload = e.newValue ? JSON.parse(e.newValue) : null;
        const meetingId = payload?.meetingId;
        if (!meetingId) return;
        if (activeMeetingIdRef.current && meetingId === activeMeetingIdRef.current) {
          handleStop();
        }
      } catch {}
    };
    window.addEventListener('storage', onStorage);
    const clean = () => {
      try { sttRef.current?.stop(); } catch {}
      try {
        const m = activeMeetingIdRef.current;
        if (m) leaveSttMeeting(m);
        const p = passiveJoinedRef.current;
        if (p && p !== m) leaveSttMeeting(p);
      } catch {}
      activeMeetingIdRef.current = null;
      passiveJoinedRef.current = null;
      try { localStorage.setItem('stt:recording', '0'); } catch {}
      try { localStorage.removeItem('stt:recording_at'); } catch {}
    };
    window.addEventListener('beforeunload', clean);
    window.addEventListener('pagehide', clean, { capture: true } as any);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('beforeunload', clean);
      window.removeEventListener('pagehide', clean as any, { capture: true } as any);
      clean();
      try { if (keepAliveRef.current) { clearInterval(keepAliveRef.current); keepAliveRef.current = null; } } catch {}
    };
  }, []);

  const addSystemInfo = (text: string) => {
    setTranscript((prev) => [
      ...prev,
      {
        id: `sys-${Date.now()}`,
        userId: 'system',
        displayName: 'System',
        text,
        atIso: new Date().toISOString(),
        final: true
      }
    ]);
  };

  const pad = (n: number) => String(n).padStart(2, '0');
  const formatDateTime = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  /**
   * Lista transkrypcji pokazuje wyłącznie spotkania „odbyte” (zakończone lub trwające),
   * dodatkowo spotkania w oknie ±5 min od startu, żeby można było startować jeszcze przed
   * formalnym rozpoczęciem wydarzenia w kalendarzu.
   */
  const filteredEvents = React.useMemo(() => {
    const now = Date.now();
    const twoHours = 120 * 60 * 1000;
    const FIVE_MIN = 5 * 60 * 1000;
    return (events || []).filter((ev) => {
      const t = ev.start ? new Date(ev.start).getTime() : NaN;
      if (Number.isNaN(t)) return false;
      const withinStartWindow = Math.abs(now - t) <= FIVE_MIN;
      const already = now >= t && now <= t + twoHours;
      const ended = now > t + twoHours;
      return ended || already || withinStartWindow;
    });
  }, [events]);

  const selectedEvent = React.useMemo(() => (events || []).find(e => e.id === selectedMeetingId) || null, [events, selectedMeetingId]);
  const canStart = !!selectedMeetingId && isEligible(selectedEvent?.start);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', mt: '64px' }}>
      {/* Lista spotkań po lewej */}
      <Box sx={{ width: 360, borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Spotkania</Typography>
          <Typography variant="body2" color="text.secondary">Wybierz spotkanie i uruchom transkrypcję</Typography>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loadingEvents ? (
            <Typography sx={{ px: 2, py: 2 }} color="text.secondary">Ładowanie…</Typography>
          ) : filteredEvents.length === 0 ? (
            <Typography sx={{ px: 2, py: 2 }} color="text.secondary">Brak nadchodzących wydarzeń</Typography>
          ) : (
            <List dense disablePadding>
              {filteredEvents.map((ev) => {
                const selected = selectedMeetingId === ev.id;
                const eligible = isEligible(ev.start);
                return (
                <ListItem
                  key={ev.id}
                  onClick={() => setSelectedMeetingId(ev.id)}
                  sx={{
                    px: 2, py: 1.25, borderBottom: '1px solid #f3f3f3',
                    backgroundColor: selected ? '#EEF2FF' : 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <ListItemText
                    primary={ev.summary || '(bez tytułu)'}
                    secondary={`${formatDateTime(ev.start)}${eligible ? '' : ' • poza oknem transkrypcji'}`}
                    primaryTypographyProps={{ sx: { fontWeight: 500 } }}
                  />
                </ListItem>
              );
              })}
            </List>
          )}
        </Box>
      </Box>

      {/* Okno transkrypcji po prawej */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Transkrypcja</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedMeetingId ? `Wybrane spotkanie: ${selectedMeetingId}` : 'Nie wybrano spotkania'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isRecording ? (
                <Button startIcon={<StopIcon />} variant="contained" color="error" onClick={handleStop}>Zatrzymaj</Button>
              ) : (
                <Button startIcon={<PlayArrowIcon />} variant="contained" disabled={!canStart} onClick={()=>selectedMeetingId && handleStart(selectedMeetingId)}>Start</Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Notatka wygeneruje się automatycznie po zatrzymaniu transkrypcji (zadanie Gemmy).
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {!isSttSupported() && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Rozpoznawanie mowy nie jest wspierane w tej przeglądarce. Najlepiej użyj Chrome.
            </Alert>
          )}
          {transcript.length === 0 ? (
            <Typography color="text.secondary">Brak treści. Uruchom transkrypcję, aby rozpocząć.</Typography>
          ) : (
            transcript.map((t) => (
              <Paper key={t.id} elevation={0} sx={{ p: 1.5, mb: 1.25, border: '1px solid #eee' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  [{formatDateTime(t.atIso)} {t.displayName || t.userId}]
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{t.text}</Typography>
              </Paper>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TranscriptionPage;


